import type { MutableRefObject } from 'react';
import { useCallback, useContext, useEffect, useRef, createContext } from 'react';
import type { Location } from 'react-router';
import { useBlocker } from 'react-router';
import { useTranslation } from 'react-i18next';

export const LeavingPageContext = createContext<MutableRefObject<boolean> | null>(null);

export const useShowPopupBeforeLeavingPage = (): { popupAvailableRef: MutableRefObject<boolean> } => {
  const { t } = useTranslation();
  const popupAvailableRef = useRef(false);

  // useBlocker を使用してナビゲーションをブロック
  const shouldBlock = useCallback(
    ({ currentLocation, nextLocation }: { currentLocation: Location; nextLocation: Location }) =>
      popupAvailableRef.current && currentLocation.pathname !== nextLocation.pathname,
    []
  );
  const blocker = useBlocker(shouldBlock);

  // ブロック状態の処理
  useEffect(() => {
    if (blocker.state === 'blocked') {
      const confirmed = window.confirm(t('canvas.page.leave.confirm'));
      if (confirmed) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker, t]);

  // ページのリロードや外部サイトへの遷移に対応
  useEffect(() => {
    const handleUnload = (ev: BeforeUnloadEvent) => {
      if (popupAvailableRef.current) {
        ev.preventDefault();
        ev.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [popupAvailableRef]);

  return { popupAvailableRef };
};

/**
 * ページ遷移をブロックするかどうかをセットするフック。
 *
 * このフックでtrue をセットした後、falseに戻さない限りページ遷移をブロックするので注意。
 * （コンポーネントのアンロードではブロック状態が消えない）
 *
 * @param condition ページ遷移をブロックする場合はtrueを指定。
 */
export const usePopupAvailable = (condition: boolean): void => {
  const popupAvailableRef = useContext(LeavingPageContext);

  useEffect(() => {
    if (!popupAvailableRef) return;

    popupAvailableRef.current = condition;
  }, [condition, popupAvailableRef]);
};
